import type { ReactNode } from 'react'
import type { Order } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import Badge from '@/components/ui/Badge'
import ProductImage from '@/components/order/ProductImage'
import ArtworkGallery from '@/components/order/ArtworkGallery'
import NameList from '@/components/order/NameList'
import { specEntries, useOptionGroups } from '@/lib/options'
import { itemArtworks } from '@/lib/artwork'

/**
 * The "Order details" card on the staff and partner job pages: the garment
 * without the money, which these payloads never carry. Rows follow the admin
 * order page (photo, labelled options, size chips) so the floor reads an order
 * the way the office does. The dates live in JobDateStrip above. `action`
 * sits at the right of the heading; the job pages put Sheet there on phones.
 */
export default function JobOrderItems({ order, action }: { order?: Order; action?: ReactNode }) {
  const optionGroups = useOptionGroups()

  return (
    <Card>
      <SectionLabel>
        <div className="flex items-center justify-between gap-2">
          Order details
          {action}
        </div>
      </SectionLabel>
      <div className="divide-y divide-line">
        {order?.items?.map((item) => (
          <div key={item.id} className="flex gap-3 py-3 text-sm first:pt-0 last:pb-0 sm:gap-4">
            <ProductImage
              product={{ name: item.product, image_url: item.image_url, image_color: item.image_color }}
              className="size-16 shrink-0 border border-line sm:size-20"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold text-foreground">{item.product}</p>
                <Badge tone="brand" className="shrink-0 font-semibold">
                  {item.qty} pcs
                </Badge>
              </div>
              {specEntries(item.spec, optionGroups.data).map((entry) =>
                entry.key === 'remark' ? (
                  <p key={entry.key} className="text-xs text-danger">
                    {entry.label}: <span className="font-semibold">{entry.value}</span>
                  </p>
                ) : (
                  <p key={entry.key} className="text-xs text-muted">
                    {entry.label}: <span className="font-medium text-foreground">{entry.value}</span>
                  </p>
                ),
              )}
              <div className="mt-1.5 flex flex-wrap gap-1">
                {Object.entries(item.size_matrix).map(([size, qty]) => (
                  <span key={size} className="rounded-md bg-foreground/5 px-2 py-0.5 text-xs text-muted">
                    {size}: <span className="font-semibold text-ink">{qty}</span>
                  </span>
                ))}
              </div>
              <NameList rows={item.personalizations ?? []} sizes={Object.keys(item.size_matrix)} className="mt-2" />
              <ArtworkGallery files={itemArtworks(item)} />
              {/* The font behind the names, for the size layout. */}
              {(item.fonts?.length ?? 0) > 0 && (
                <div className="mt-2">
                  <p className="text-2xs font-semibold uppercase tracking-wide text-muted">Font file</p>
                  <ArtworkGallery files={item.fonts!} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {order?.notes && (
        <p className="mt-3 rounded-lg border border-dashed border-brand-400 bg-brand-50 p-2.5 text-xs">
          <span className="font-bold">Production notes:</span> {order.notes}
        </p>
      )}
    </Card>
  )
}
